import React, { useState } from 'react';
import { ChevronDown, Circle, CircleCheck, CirclePlay } from 'lucide-react';
import ProgressBar from './ProgressBar';
import LessonLine from './LessonLine';
import { EnrollmentBadge } from './StatusChips';
import { agoText, describeInstant, formatDate, formatPosition, ucfirst } from './time';
import { useI18n } from '../../i18n/I18nContext';
import './Student360.css';

const LESSON_ICON = {
  COMPLETED: <CircleCheck size={16} aria-hidden="true" />,
  IN_PROGRESS: <CirclePlay size={16} aria-hidden="true" />,
  NOT_STARTED: <Circle size={16} aria-hidden="true" />,
};

/** The learning facet of one course: module by module, lesson by lesson. */
function LessonTable({ course, currentLessonId, now }) {
  const { t } = useI18n();
  return (
    <div className="s360-lessons">
      {course.modules.map((module) => (
        <section key={module.moduleId ?? `module-${module.orderIndex}`} className="s360-module">
          <h4 className="s360-module-title">
            <span>{module.title || t('student360.course.noModule')}</span>
            <span className="s360-muted">
              {`${module.progress.completedLessons} / ${module.progress.totalLessons}`}
            </span>
          </h4>
          <ul className="s360-lesson-list">
            {module.lessons.map((lesson) => (
              <li
                key={lesson.lessonId}
                className={`s360-lesson-row is-${lesson.status.toLowerCase().replace('_', '-')} ${lesson.lessonId === currentLessonId ? 'is-current' : ''}`}
                aria-current={lesson.lessonId === currentLessonId ? 'true' : undefined}
              >
                <span className="s360-lesson-icon">{LESSON_ICON[lesson.status] || LESSON_ICON.NOT_STARTED}</span>
                <span className="s360-lesson-name" title={lesson.title}>
                  {`${lesson.index}. ${lesson.title}`}
                </span>
                <span className="s360-lesson-state">
                  {t(`student360.course.lessonStatus.${lesson.status}`)}
                  {lesson.status === 'IN_PROGRESS' && lesson.position > 0
                    ? ` · ${formatPosition(lesson.position)}`
                    : ''}
                </span>
                <span className="s360-lesson-when">
                  {lesson.touchedAt ? agoText(t, describeInstant(lesson.touchedAt, now)) : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * One course of the student: progress, where they are (when the viewer may see lesson detail),
 * when they last touched a lesson and, if the learning facet came back, the lesson table behind a
 * toggle. Every part is drawn only when its key is in the response.
 */
function CourseCard({ enrollment, course, now, focus = false }) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const full = enrollment.currentLesson !== undefined;
  const hasLessons = Boolean(course?.modules?.length);

  return (
    <article className={`s360-course ${focus ? 'is-focus' : ''}`}>
      <header className="s360-course-head">
        <h3 className="s360-course-title" title={enrollment.title}>{enrollment.title}</h3>
        <EnrollmentBadge status={enrollment.status} />
      </header>

      <ProgressBar value={enrollment.progressPercent} label={enrollment.title} />
      {typeof enrollment.completedLessons === 'number' && (
        <p className="s360-course-lessons">
          {t('student360.course.lessonsDone', {
            done: enrollment.completedLessons,
            total: enrollment.totalLessons,
          })}
        </p>
      )}

      {full && (
        <div className="s360-course-current">
          <span className="s360-label">{t('student360.lesson.current')}</span>
          <LessonLine lesson={enrollment.currentLesson} now={now} />
        </div>
      )}

      <dl className="s360-facts">
        <div>
          <dt>{t('student360.course.enrolled')}</dt>
          <dd>{formatDate(enrollment.enrolledAt, lang)}</dd>
        </div>
        {enrollment.completedAt && (
          <div>
            <dt>{t('student360.course.completed')}</dt>
            <dd>{formatDate(enrollment.completedAt, lang)}</dd>
          </div>
        )}
        {full && (
          <div>
            <dt>{t('student360.course.lastActivity')}</dt>
            <dd>
              {enrollment.lastLessonActivityAt
                ? ucfirst(agoText(t, describeInstant(enrollment.lastLessonActivityAt, now)))
                : t('student360.course.noActivity')}
            </dd>
          </div>
        )}
      </dl>

      {hasLessons && (
        <>
          <button
            type="button"
            className="s360-toggle"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
          >
            <ChevronDown size={15} className={open ? 'is-open' : ''} aria-hidden="true" />
            {open ? t('student360.course.hideLessons') : t('student360.course.showLessons')}
          </button>
          {open && (
            <LessonTable course={course} currentLessonId={enrollment.currentLesson?.lessonId} now={now} />
          )}
        </>
      )}
    </article>
  );
}

export default CourseCard;
